import CloseIcon from "@mui/icons-material/Close";
import {
  Alert,
  Box,
  CircularProgress,
  Dialog,
  IconButton,
  Tooltip,
  Typography,
} from "@mui/material";
import ConfirmDialog from "components/common/ConfirmDialog";
import CustomButton from "components/common/CustomButton";
import { useEffect, useState } from "react";

type LiveView = {
  task_id: number;
  status: string;
  phase?: string;
  instruction?: string;
  live_view_url?: string | null;
  control_url?: string | null;
  error_message?: string;
};

type Props = {
  open: boolean;
  liveView: LiveView | null;
  taskLabel: string;
  canControl: boolean;
  releasing: boolean;
  onRelease: () => void;
  onClose: () => void;
};

export const LIVE_VIEW_WARNING =
  "Clicking or typing in the bot's browser breaks the run. Use this window to watch a login or to debug a login error.";

/**
 * The operator's window into a running bot's browser.
 *
 * It opens to watch only: the backend hands out a view-only address, served by
 * a view-only VNC server, so nothing typed here reaches the bot. Control is a
 * second, confirmed step. While a captcha waits for a human the address is the
 * controlling one from the start, as it always was.
 *
 * Sends nothing back: the operator moves the real pointer over VNC and the bot
 * notices on its own that the challenge is behind it. Giving up is the only
 * command, and it costs the login attempt.
 */
export default function CaptchaLiveViewDialog({
  open,
  liveView,
  taskLabel,
  canControl,
  releasing,
  onRelease,
  onClose,
}: Props) {
  const [confirmRelease, setConfirmRelease] = useState(false);
  const [confirmControl, setConfirmControl] = useState(false);
  const [controlTaken, setControlTaken] = useState(false);
  // Held across polls so a poll without a URL cannot blank the iframe and drop
  // the operator's VNC session. A genuinely new URL is followed: a new session
  // on the same task retires the old token, and taking control swaps the
  // view-only address for the controlling one.
  const [liveUrl, setLiveUrl] = useState<string | null>(null);

  const captcha = (liveView?.phase || "").toUpperCase() === "CAPTCHA";
  const url =
    (controlTaken && !captcha ? liveView?.control_url : liveView?.live_view_url) ??
    null;
  useEffect(() => {
    if (!open) {
      setLiveUrl(null);
      setControlTaken(false);
      return;
    }
    if (url && url !== liveUrl) setLiveUrl(url);
  }, [open, url, liveUrl]);

  const interactive = captcha || controlTaken;

  return (
    <>
      <Dialog fullScreen open={open} onClose={() => undefined}>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            flexWrap: "wrap",
            gap: 2,
            px: 2,
            py: 1,
            borderBottom: 1,
            borderColor: "divider",
          }}
        >
          <Typography variant="subtitle1" fontWeight={700} sx={{ flexGrow: 1 }}>
            Live browser - {taskLabel}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {captcha
              ? liveView?.instruction ||
                "Solve the captcha here; the bot continues by itself."
              : controlTaken
                ? "You have control of the bot's browser."
                : "Watching only."}
          </Typography>
          {canControl && !captcha && !controlTaken && (
            <CustomButton
              fullWidth={false}
              onClick={() => setConfirmControl(true)}
            >
              Take control
            </CustomButton>
          )}
          {canControl && !captcha && controlTaken && (
            <CustomButton fullWidth={false} onClick={() => setControlTaken(false)}>
              Back to watching
            </CustomButton>
          )}
          {canControl && captcha && (
            <CustomButton
              fullWidth={false}
              disabled={releasing}
              onClick={() => setConfirmRelease(true)}
            >
              Give up on this challenge
            </CustomButton>
          )}
          <Tooltip title="Hide (the bot keeps running)">
            <IconButton onClick={onClose} aria-label="Hide live view">
              <CloseIcon />
            </IconButton>
          </Tooltip>
        </Box>
        {!captcha && (
          <Alert severity={controlTaken ? "error" : "warning"} sx={{ borderRadius: 0 }}>
            {LIVE_VIEW_WARNING}
          </Alert>
        )}
        <Box sx={{ flexGrow: 1, position: "relative", bgcolor: "black" }}>
          {liveUrl ? (
            <iframe
              key={interactive ? "control" : "view"}
              title="Bot browser live view"
              src={liveUrl}
              style={{
                border: 0,
                width: "100%",
                height: "100%",
                display: "block",
              }}
            />
          ) : (
            <Box
              sx={{
                position: "absolute",
                inset: 0,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: 2,
                color: "common.white",
              }}
            >
              <CircularProgress size={24} color="inherit" />
              <Typography>Connecting to the bot's browser...</Typography>
            </Box>
          )}
          {liveView?.error_message ? (
            <Alert
              severity="error"
              sx={{ position: "absolute", inset: "auto 16px 16px" }}
            >
              {liveView.error_message}
            </Alert>
          ) : null}
        </Box>
      </Dialog>

      <ConfirmDialog
        open={confirmControl}
        title="Take control of the bot's browser"
        message={`${LIVE_VIEW_WARNING} The bot keeps running while you have control.`}
        confirmLabel="Take control"
        cancelLabel="Keep watching"
        onConfirm={() => {
          setConfirmControl(false);
          setControlTaken(true);
        }}
        onCancel={() => setConfirmControl(false)}
      />

      <ConfirmDialog
        open={confirmRelease}
        title="Give up on this challenge"
        message="The bot stops waiting and starts the login over, which means a fresh 2FA code. Usually it is quicker to ask hCaptcha for another puzzle with its own reload button."
        confirmLabel="Give up"
        cancelLabel="Keep trying"
        loading={releasing}
        onConfirm={() => {
          setConfirmRelease(false);
          onRelease();
        }}
        onCancel={() => setConfirmRelease(false)}
      />
    </>
  );
}
